import { FC } from 'react';
import { useRouter } from 'next/router';
import { useSetData } from '@/data';
import { useAPI } from '@/services';
import type { IEtfTransaction } from '@/services/apis/trade/types';
import { TransactionCard } from '@/modules/invest/subModules/fund/components/TransactionCard';
import { useGetFundCodeFromQuery } from '@/modules/invest/hooks/useGetFundCodeFromQuery';
import { getEtfTransactionStatusVariant } from '@/modules/invest/utils/getEtfTransactionStatusVariant';
import { getEtfTransactionOrderSideTitle } from './PendingEtfTransactions.utils';
import type { TTransactionCardType } from '@/modules/invest/subModules/fund/components/TransactionCard/TransactionCard.types';
import { PendingTransactionsWrapper } from '../PendingTransactionsWrapper';

const PENDING_TRANSACTIONS_PAGE_SIZE = 20;

export const PendingEtfTransactions: FC = () => {
  const { push } = useRouter();

  const fundCode = useGetFundCodeFromQuery();

  const { data } = useAPI(['trade', 'getEtfTransactions'], {
    input: {
      PageIndex: 0,
      PageSize: PENDING_TRANSACTIONS_PAGE_SIZE,
      Isin: fundCode,
      orderStates: ['New', 'Doing'],
    },
  });

  const setFundIssuanceData = useSetData('etfTransaction');

  const handleTransactionClick =
    (transaction: IEtfTransaction, pageIndex: number) => async () => {
      setFundIssuanceData({ ...transaction, pageIndex });

      const transactionId = transaction.trackId || transaction.createdDate;

      await push({
        pathname: `/invest/etf/transactions/${transactionId}`,
      });
    };

  return (
    <PendingTransactionsWrapper transactionLength={data?.items.length}>
      {data?.items.map((transaction, index) => {
        const title = getEtfTransactionOrderSideTitle(transaction.orderSide);

        const variant = getEtfTransactionStatusVariant(transaction.orderState);

        const type: TTransactionCardType =
          transaction.orderSide === 'Buy' ? 'Issuance' : 'Redemption';

        return (
          <TransactionCard
            key={transaction.trackId}
            amount={transaction.approvedAmount || transaction.amount}
            chipTitle={transaction.orderStateTitle}
            chipVariant={variant}
            date={transaction.createdDate}
            title={title}
            type={type}
            onClick={handleTransactionClick(transaction, index)}
          />
        );
      })}
    </PendingTransactionsWrapper>
  );
};
